import React, { useEffect, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useChat } from "../../contexts/ChatContext";
import CircularProgress from "../utils/CircularProgress";
import rehypeRaw from "rehype-raw";

function MessageList() {
  const { state } = useChat();
  const { messages = [], isLoading } = state;
  const containerRef = useRef(null);
  const [isAutoScrolling, setIsAutoScrolling] = useState(true);
  const prevScrollTop = useRef(0); // To track the previous scroll position

  // Handle automatic scrolling
  useEffect(() => {
    if (isAutoScrolling) {
      containerRef.current.scrollTo({
        top: containerRef.current.scrollHeight,
        behavior: "smooth",
      });
    }
  }, [messages, isAutoScrolling]);

  // Stop auto-scroll on user interaction
  const handleScroll = () => {
    const container = containerRef.current;
    const currentScrollTop = container.scrollTop;

    // Check if the user scrolled upward
    if (currentScrollTop < prevScrollTop.current && isAutoScrolling) {
      setIsAutoScrolling(false); // User interrupted auto-scroll
    }

    // Update the previous scroll position
    prevScrollTop.current = currentScrollTop;
  };

  // Resume auto-scrolling when the assistant is sending data
  useEffect(() => {
    if (isLoading) {
      setIsAutoScrolling(true); // Re-enable auto-scrolling on new assistant response
    }
  }, [isLoading]);

  return (
    <div
      ref={containerRef}
      onScroll={handleScroll}
      className="flex-1 overflow-y-auto p-4  sm:mx-0 sm:w-full md:mx-auto md:w-[80%] lg:w-[60%]"
    >
      {messages.map((msg, index) => (
        <div
          key={index}
          className={`mb-4 flex ${
            msg.role === "user" ? "justify-end" : "justify-start"
          } mx-11`}
        >
          <div
            className={`max-w-xs md:max-w-lg 
                ${msg.role === "user" ? "p-3 md:p-4" : "p-4 md:p-5"}
                rounded-3xl shadow-lg ${
                  msg.role === "user"
                    ? "bg-blue-600 text-gray-300"
                    : "bg-gray-600 text-gray-300"
                }`}
            style={{
              backgroundColor:
                msg.role === "user"
                  ? "#607D8B" // Custom user bubble color for contrast
                  : "#1F2937", // Assistant bubble matches the background theme
            }}
          >
            {msg.role === "user" ? (
              <span className="text-base sm:text-sm">{msg.content}</span>
            ) : (
              <ReactMarkdown
              /** Using rehypeRaw plugins for proper formatting - line breaks, etc. */
                rehypePlugins={[rehypeRaw]}
                className="text-base sm:text-sm"
                children={msg.content}
              />
            )}
          </div>
        </div>
      ))}
      {isLoading && (
        <div className="flex justify-center items-center mt-4">
          <CircularProgress />
        </div>
      )}
    </div>
  );
}

export default MessageList;
